import React from 'react';
import lineReaderSvg from 'chameleon/packages/blue-book-player/library/icon-list/raw-icons/line-reader.svg';
import Icon from 'chameleon/packages/blue-book-player/library/icon/Icon';

const HELP_ITEMS = [
  {
    title: 'Zoom and Magnification',
    description: (
      <>
        <div className="font-semibold mb-10">
          Chromebook Zoom and Magnification
        </div>
        <div className="mb-10">
          To zoom in/out, press{' '}
          <span className="font-semibold">Control +/-</span>. To reset zoom,
          press
          <span className="font-semibold"> Control + 0</span>.
        </div>
        <div className="leading-10 mb-10">
          For touchscreen and touchpad, pinch in/out.
        </div>
        <div className="leading-10">
          To use the magnification feature, select the options from the floating
          accessibility menu:
          <ul className="mt-0">
            <li>
              Full-screen magnifier (
              <span className="font-semibold">Search + Ctrl + M</span>) --
              whole-screen magnification
              <ul>
                <li>
                  Adjust the full-screen magnifier from the floating{' '}
                  <span className="font-semibold">
                    Accessibility menu &gt; Settings &gt; Display and
                    magnification &gt; Full screen magnifier &gt; select any of
                    these options
                  </span>
                  :
                  <ul>
                    <li>Move screen continuously as mouse moves</li>
                    <li>Move screen keeping mouse at center of screen</li>
                    <li>
                      Move screen when mouse touches edge of screen (default)
                    </li>
                  </ul>
                </li>
              </ul>
            </li>
            <li>
              Docked magnifier (
              <span className="font-semibold">Search + Ctrl + D</span>) --
              split-screen magnification
              <ul>
                <li>
                  To adjust zoom level, select the down arrow in the{' '}
                  <span className="font-semibold">Docked zoom level</span>.
                </li>
                <li>
                  To change what&apos;s magnified, move your pointer or press{' '}
                  <span className="font-semibold">Tab</span>.
                </li>
              </ul>
            </li>
          </ul>
        </div>
        <div className="font-semibold mb-10 mt-16">
          Windows Zoom and Magnifier
        </div>
        <div className="mb-10">
          To zoom in/out, press{' '}
          <span className="font-semibold">Control +/-</span>. To reset zoom,
          press
          <span className="font-semibold"> Control + 0</span>.
        </div>
        <div className="leading-10 mb-10">
          For touchscreen and touchpad, pinch in/out.
        </div>
        <div>To use Windows Magnifier:</div>
        <ul className="my-0 leading-10">
          <li>
            Windows 10: Select{' '}
            <span className="font-semibold">
              Start&gt;Settings&gt;Ease of Access&gt;Magnifier&gt;Turn on
              Magnifier
            </span>
            .
          </li>
          <li>
            Windows 10: Select{' '}
            <span className="font-semibold">
              Start&gt;Settings&gt;Ease of Access&gt;Magnifier&gt;Turn on
              Magnifier
            </span>
            .
          </li>
          <li>
            Or press the{' '}
            <span className="font-semibold">indows logo key+plus sign (+)</span>
            . To turn off the Magnifier, press the{' '}
            <span className="font-semibold">Windows logo key+Esc</span>.
          </li>
        </ul>
        <div>Adjust the Magnifier view per your preference:</div>
        <ul className="mt-0 leading-10">
          <li>
            Lens (<span className="font-semibold">Ctrl+Alt+L</span>)
          </li>
          <li>
            Full screen (<span className="font-semibold">Ctrl+Alt+F</span>)
          </li>
          <li>
            Docked (<span className="font-semibold">Ctrl+Alt+D</span>)
          </li>
        </ul>
        <div className="font-semibold mb-10 mt-16">MacOS Zoom</div>
        <div className="mb-10">
          To zoom in/out, press{' '}
          <span className="font-semibold">Command +/-</span>. To reset zoom,
          press
          <span className="font-semibold"> Command + 0</span>.
        </div>
        <div className="leading-10 mb-10">
          For touchscreen and touchpad, pinch in/out.
        </div>
        <div className="mb-10">
          To customize how the zoom area appears, go to{' '}
          <span className="font-semibold">
            Apple menu&gt;System Settings&gt;Accessibility
          </span>{' '}
          and click{' '}
          <span className="font-semibold">
            Zoom on the right (keyboard shortcut: Option+Command+8)
          </span>
          .
        </div>
        <div>
          In the Advanced Options, select the checkboxes for your zoom
          preference:
        </div>
        <ul className="my-0 leading-10">
          <li>Use keyboard shortcuts to zoom</li>
          <li>Use trackpad gesture to zoom</li>
          <li>Use scroll gesture with modifier keys to zoom</li>
        </ul>
        <div>Select Zoom style options per student preference:</div>
        <ul className="my-0 leading-10">
          <li>Full screen</li>
          <li>Split screen</li>
          <li>Picture-in-picture</li>
        </ul>
        <div className="font-semibold mb-10 mt-16">iPad Zoom</div>
        <div className="mb-10">Pinch to zoom in or zoom out.</div>
        <div className="mb-10">
          For additional zoom options in the iPad, tap{' '}
          <span className="font-semibold">
            Settings&gt;Accessibility&gt;Zoom
          </span>
          .
        </div>
        <div>Adjust any of the following:</div>
        <ul className="my-0 leading-10">
          <li>
            Keyboard shortcuts: Control zoom using shortcuts on an external
            keyboard.
          </li>
          <li>
            Zoom controller: Turn the controller on, set controller actions, and
            adjust the color and opacity.
          </li>
          <li>
            Zoom region: Choose Pinned Zoom, Full Screen Zoom, or Window Zoom.
          </li>
          <li>
            Zoom filters: Choose None, Inverted, Grayscale, Grayscale Inverted,
            or Low Light.
          </li>
          <li>Maximum zoom level: Drag the slider to adjust the level.</li>
        </ul>
      </>
    ),
  },
  {
    title: 'Line Reader',
    description: (
      <div className="leading-10">
        <img
          className="mr-4 align-middle"
          aria-hidden="true"
          src={lineReaderSvg}
          alt="line reader icon"
        />
        <span className="font-semibold">Line Reader</span>: This tool helps you
        focus as you&apos;re reading test content. Click the six dots at the top
        of the line reader to move it up and down using your keyboard arrows. If
        you&apos;re on a touch screen device, you can also move it with your
        finger. To see more than one line of text at a time, use the arrow
        button at the bottom of the line reader to change the number of visible
        lines.
      </div>
    ),
  },
  {
    title: 'Option Eliminator',
    description: (
      <div className="leading-10">
        <Icon
          id="crossOut"
          className="fill-white [--current-text-color:#000] mr-4 align-middle"
        />
        <span className="font-semibold">Option Eliminator</span>: On any
        multiple-choice question, you can cross out answer options you think are
        wrong. Above the answer, click the{' '}
        <span className="font-semibold">ABC</span> button, then you&apos;ll see
        a button next to each answer option, allowing you to cross it out. You
        can always undo this action.
      </div>
    ),
  },
  {
    title: 'Mark for Review',
    description: (
      <div className="leading-10">
        <Icon id="bookmark" className="fill-white mr-4 align-middle" />
        <span className="font-semibold">Mark for Review</span>: Flag any
        question you want to come back to later.
      </div>
    ),
  },
  {
    title: 'Question Menu',
    description:
      "At the bottom of each testing screen, you'll find a menu that allows you to navigate to any question in the section. You'll see which questions you've answered, which you haven't, and any that you've marked for review.",
  },
  {
    title: 'No Penalty for Guessing',
    description:
      'Points are never deducted for wrong answers. So, on your AP Exam, it will always be better to guess than to leave an answer blank. For your AP Classroom practice, consider that guessing can affect whether assignment results are giving you and your teacher an accurate indication of your strengths and areas for improvement.',
  },
];

export const HELP_ITEMS_BLUEBOOK_DIGITAL_EXAM = [
  {
    title: 'Submitting Your Answers',
    description: (
      <div className="leading-10">
        In AP Classroom, you can always submit your assignment using the{' '}
        <span className="font-semibold">Submit </span>
        button under the <span className="font-semibold">More </span>menu. Your
        assignment may be submitted automatically at its due date, or if your
        teacher closes the assignment.
        <div>
          On your AP Exam, you will have to wait until time expires, at which
          point your answers will be submitted automatically.
        </div>
      </div>
    ),
  },
  {
    title: 'AP Exam Preparation',
    description: (
      <div className="leading-10">
        <div>
          AP Classroom has updated the look and feel of its online test-taking
          experience and added features so you can begin to familiarize yourself
          with the question-responding experience of Bluebook™, the digital AP
          Exam application.
        </div>
        <div>
          While AP Classroom practice assessments will prepare you for the
          experience of responding to digital AP Exam questions, there are some
          differences. The best representation of the exam experience you will
          have on test day is Bluebook’s Test Preview.
        </div>
        <div>
          When you get closer to the exam, you can further familiarize yourself
          with the test-taking experience that will be available on test day,
          including seeing section instructions and scheduled breaks, by{' '}
          <a
            href="https://bluebook.collegeboard.org/students/download-bluebook"
            target="_blank"
            rel="noreferrer"
          >
            downloading Bluebook
          </a>{' '}
          and using the Test Preview for your subject.
        </div>
      </div>
    ),
  },
];

export const HELP_ITEMS_NO_BLUEBOOK = [
  {
    title: 'Submitting Your Answers',
    description: (
      <div className="leading-10">
        In AP Classroom, you can always submit your assignment using the{' '}
        <span className="font-semibold">Submit </span>
        button under the <span className="font-semibold">More </span>menu. Your
        assignment may be submitted automatically at its due date, or if your
        teacher closes the assignment.
      </div>
    ),
  },
];

export default HELP_ITEMS;
